/**
 * Region gates cache
 *
 * IndexedDB-backed store for the region allow-lists that gate the GitHub and
 * Azure DevOps built-in connectors — mirroring how the operations corpus is
 * cached (see operationsCache.ts). Runtime-tunable from the browser without a
 * code change: edit the `otto-settings` DB in devtools or call
 * {@link writeRegionGates}. Best-effort — every op swallows errors and falls
 * back to {@link DEFAULT_REGION_GATES} so a failing IndexedDB never hides
 * connectors.
 */

import type { RegionGates } from './operationsService';

const DB_NAME = 'otto-settings';
const DB_VERSION = 1;
const STORE_NAME = 'settings';
const RECORD_KEY = 'region-gates';

/** Built-in defaults used until an override is written. Keyed by connector id. */
export const DEFAULT_REGION_GATES: RegionGates = {
  '/serviceproviders/github': [],
  '/serviceproviders/ado-pipelines': [],
  '/serviceproviders/ado-repos': [],
  '/serviceproviders/ado-build': [],
  '/serviceproviders/ado-boards': [],
};

/**
 * Dispatched on `window` after a successful {@link writeRegionGates}. Listeners
 * (e.g. usePrefetchOperations) invalidate the built-in operations query so a
 * change applies without reopening the picker.
 */
export const REGION_GATES_CHANGED_EVENT = 'otto:region-gates-changed';

type RegionGatesRecord = { key: string; gates: RegionGates };

let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      console.warn('[regionGatesCache] indexedDB.open failed:', err);
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn('[regionGatesCache] indexedDB open error:', request.error);
      resolve(null);
    };
    request.onblocked = () => {
      // Another tab holds an older version open — retry on next access.
      dbPromise = null;
      resolve(null);
    };
  });
  return dbPromise;
};

const requestToPromise = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/** Coerce an untrusted value into a valid gate map, or null if unusable. */
const asGates = (value: unknown): RegionGates | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out: RegionGates = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      out[k.toLowerCase()] = v as string[];
    }
  }
  return Object.keys(out).length > 0 ? out : null;
};

const getRecord = (db: IDBDatabase): Promise<RegionGatesRecord | undefined> =>
  new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readonly');
    } catch {
      resolve(undefined);
      return;
    }
    requestToPromise(
      tx.objectStore(STORE_NAME).get(RECORD_KEY) as IDBRequest<
        RegionGatesRecord | undefined
      >,
    ).then(resolve, () => resolve(undefined));
  });

const putRecord = (db: IDBDatabase, gates: RegionGates): Promise<boolean> =>
  new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, 'readwrite');
    } catch (err) {
      console.warn('[regionGatesCache] write transaction failed:', err);
      resolve(false);
      return;
    }
    const record: RegionGatesRecord = { key: RECORD_KEY, gates };
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
    tx.onabort = () => resolve(false);
  });

/** Read the stored gates, or {@link DEFAULT_REGION_GATES} when unset/unreadable. */
export const readRegionGates = async (): Promise<RegionGates> => {
  const db = await openDb();
  if (!db) return DEFAULT_REGION_GATES;
  try {
    const record = await getRecord(db);
    return asGates(record?.gates) ?? DEFAULT_REGION_GATES;
  } catch (err) {
    console.warn('[regionGatesCache] read failed:', err);
    return DEFAULT_REGION_GATES;
  }
};

/**
 * Seed the record with {@link DEFAULT_REGION_GATES} when it doesn't exist yet,
 * so the row is always present and editable in the Application → IndexedDB
 * panel. Does NOT dispatch {@link REGION_GATES_CHANGED_EVENT} — seeding the
 * defaults changes no behavior, so there's nothing to re-fetch.
 */
export const seedRegionGatesIfMissing = async (): Promise<void> => {
  const db = await openDb();
  if (!db) return;
  if ((await getRecord(db)) === undefined) {
    await putRecord(db, DEFAULT_REGION_GATES);
  }
};

/** Persist a new set of gates. Best-effort; dispatches the change event only
 * after the write actually commits so listeners don't refetch stale gates. */
export const writeRegionGates = async (gates: RegionGates): Promise<void> => {
  const db = await openDb();
  if (!db) return;
  const committed = await putRecord(db, gates);
  if (committed && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(REGION_GATES_CHANGED_EVENT));
  }
};

/** Test-only: reset the memoized DB handle so a fresh open runs. */
export const __resetRegionGatesDbForTests = (): void => {
  dbPromise = null;
};
